import { encodeResearchAgentResourceId } from "@repo/app-types/entities/researchAgent";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { useHideAlpharunBranding } from "../../helpers/fountain.js";
import { useMainNavigationFeatureFlags } from "../../helpers/mainNavigation.js";
import {
  readStoredBooleanPreference,
  writeStoredBooleanPreference,
} from "../../helpers/storedPreference.js";
import { useAppSelector } from "../../hooks/hook.js";
import { MastraChatProvider } from "../agents/mastra/mastraChatContext.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import {
  RESEARCH_THREAD_ID_PARAM,
  ResearchAgentContext,
  type ResearchAgentProps,
} from "./researchAgentContext.js";
import { useConfigurationPageArrival } from "./researchAgentPageContext.js";
import { useIsDockedPanelViewport } from "./researchAgentViewport.js";
import { useCopilotChat } from "./useCopilotChat.js";

export interface ResearchAgentProviderProps {
  children?: ReactNode;
}

/** localStorage key remembering whether the side panel was left open. */
const panelExpandedStorageKey = "alpharun.researchAgentExpanded";

export const ResearchAgentProvider: FC<ResearchAgentProviderProps> = (
  props,
) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(() =>
    readStoredBooleanPreference(panelExpandedStorageKey),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isDockedPanelViewport = useIsDockedPanelViewport();

  useEffect(() => {
    writeStoredBooleanPreference(panelExpandedStorageKey, isExpanded);
  }, [isExpanded]);

  // Availability is decided here rather than at the button, so that the
  // surfaces that open the chat without it — a `?research_thread_id=` link and
  // a panel restored from the last visit — cannot reach an agent this company
  // doesn't get.
  const isEnabledForCompany = useIsResearchAgentEnabledForCompany();
  const teammateResourceId = useResearchAgentResourceId();
  const resourceId = isEnabledForCompany ? teammateResourceId : undefined;

  // Work the page needs finished before a message goes out, registered by the
  // editor that owns it. Held in a ref so registering re-renders nothing.
  const beforeSendRef = useRef<Set<() => Promise<unknown>>>(new Set());
  const registerBeforeSend = useCallback(
    (beforeSend: () => Promise<unknown>) => {
      beforeSendRef.current.add(beforeSend);
      return () => {
        beforeSendRef.current.delete(beforeSend);
      };
    },
    [],
  );

  // A page's unsaved edits are what the user is asking about. A failed flush
  // reports itself in that page's own editor, so the send proceeds against
  // whatever is stored.
  const flushBeforeSend = useCallback(
    () =>
      Promise.all(
        [...beforeSendRef.current].map((beforeSend) =>
          beforeSend().catch(() => undefined),
        ),
      ),
    [],
  );

  const {
    copilotChat,
    mastraChat,
    openThread,
    openNewThread,
    forgetNotFoundThread,
  } = useCopilotChat({ resourceId, onBeforeSend: flushBeforeSend });

  // Arriving on a configuration page opens the panel on a new chat. Closing
  // the panel sticks until the next arrival.
  useConfigurationPageArrival((keepsCopilotChat) => {
    setIsExpanded(true);
    // Already a new chat, or the navigation keeps the current one
    if (!copilotChat?.threadId || keepsCopilotChat) {
      return;
    }
    openNewThread();
  });

  // Which surface hosts the chat depends on the viewport: the docked panel
  // can't fit on narrow ones, where the expanded modal stands in for it.
  // Callers open and close the chat through these rather than picking.
  const onOpenChat = useCallback(() => {
    if (isDockedPanelViewport) {
      setIsExpanded(true);
    } else {
      setIsModalOpen(true);
    }
  }, [isDockedPanelViewport]);

  // A thread we could not load isn't worth restoring when the chat reopens.
  const onCloseChat = useCallback(() => {
    setIsExpanded(false);
    setIsModalOpen(false);
    forgetNotFoundThread();
  }, [forgetNotFoundThread]);

  const onModalOpenChange = useCallback(
    (newIsModalOpen: boolean) => {
      setIsModalOpen(newIsModalOpen);
      if (!newIsModalOpen) {
        forgetNotFoundThread();
      }
    },
    [forgetNotFoundThread],
  );

  // Sync the currently visible thread to a URL query string parameter
  const [searchParams, setSearchParams] = useSearchParams();
  const currentThreadIdParam = searchParams.get(RESEARCH_THREAD_ID_PARAM);
  const didHandleThreadIdParam = useRef(false);

  // If there's a thread ID in the URL upon mounting, load that thread. A link
  // always opens the modal, whatever the viewport: someone following a shared
  // chat came for the conversation, not for the page behind it.
  useEffect(() => {
    if (didHandleThreadIdParam.current) {
      return;
    }

    // Wait for the resource id rather than settling the ref without it: it is
    // undefined until the teammate loads, and stays undefined for a company
    // that doesn't get the agent at all.
    if (!resourceId) {
      return;
    }

    if (!currentThreadIdParam) {
      didHandleThreadIdParam.current = true;
      return;
    }

    setIsModalOpen(true);

    if (currentThreadIdParam === "new") {
      didHandleThreadIdParam.current = true;
      return;
    }

    void openThread(currentThreadIdParam).finally(() => {
      didHandleThreadIdParam.current = true;
    });
  }, [currentThreadIdParam, openThread, resourceId]);

  const onThreadIdParamChange = useEffectEvent(
    (newThreadIdParam: string | null) => {
      // Don't call `setSearchParams` at all unless we have to, because it wipes out stuff like the PostHog dev toolbar auth URL hash.
      if (currentThreadIdParam === newThreadIdParam) {
        return;
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newThreadIdParam === null) {
            next.delete(RESEARCH_THREAD_ID_PARAM);
          } else {
            next.set(RESEARCH_THREAD_ID_PARAM, newThreadIdParam);
          }
          return next;
        },
        { replace: true },
      );
    },
  );

  const threadId = copilotChat?.threadId;
  // The URL mirrors the modal and nothing else. The docked panel is a piece of
  // app furniture the user leaves open across navigations, so writing to the
  // URL from it would rewrite the address bar as a side effect of scrolling
  // through chats, and hand out links carrying a thread the sender never meant
  // to share. The modal is the deliberate, shareable surface.
  const isResearchAgentAvailable = resourceId !== undefined;
  const isPanelVisible = isExpanded && isDockedPanelViewport;
  const isChatVisible = isPanelVisible || isModalOpen;
  const threadIdParam =
    isResearchAgentAvailable && isModalOpen ? (threadId ?? "new") : null;
  useEffect(() => {
    onThreadIdParamChange(threadIdParam);
  }, [threadIdParam, onThreadIdParamChange]);

  const contextValue: ResearchAgentProps | undefined = useMemo(() => {
    // Show the research agent only when we were able to calculate a Mastra
    // resourceId, which indicates the user has access.
    if (!copilotChat) {
      return undefined;
    }

    return {
      ...copilotChat,
      isPanelVisible,
      isChatVisible,
      onOpenChat,
      onCloseChat,
      isModalOpen,
      onModalOpenChange,
      registerBeforeSend,
    };
  }, [
    copilotChat,
    isPanelVisible,
    isChatVisible,
    onOpenChat,
    onCloseChat,
    isModalOpen,
    onModalOpenChange,
    registerBeforeSend,
  ]);

  return (
    <ResearchAgentContext.Provider value={contextValue}>
      <MastraChatProvider chat={mastraChat}>
        {props.children}
      </MastraChatProvider>
    </ResearchAgentContext.Provider>
  );
};

/**
 * Whether the company gets the research agent at all. It answers over the
 * non-recruiting workspaces, so it needs at least one of them, and it carries
 * Alpharun branding, so it follows the same whitelabeling rule as the logo.
 */
function useIsResearchAgentEnabledForCompany() {
  const { hasTeamPerformanceTab, hasTraining, hasAgentsTab } =
    useMainNavigationFeatureFlags();
  const hideAlpharunBranding = useHideAlpharunBranding();

  return (
    (hasTeamPerformanceTab || hasTraining || hasAgentsTab) &&
    !hideAlpharunBranding
  );
}

/**
 * Determines the Mastra `resourceId` for the current teammate.
 *
 * What a teammate's role lets the agent read is decided server-side, by the
 * `teammateScope` the agent's dependencies derive from that role — a team
 * member's tools are bound to their own work. So the route the user is on
 * does not decide whether they get the agent.
 */
function useResearchAgentResourceId() {
  const authenticatedTeammate = useAppSelector(selectTeammate);

  // If we don't have a teammate associated with a company, we cannot show the research agent.
  if (!authenticatedTeammate?.company_id) {
    return undefined;
  }

  return encodeResearchAgentResourceId(
    authenticatedTeammate.company_id,
    authenticatedTeammate.id,
  );
}
